import { buildCliFlags, formatCliLine } from './buildCliFlags'
import { coerceAll } from './coerceParam'
import type { AccountForm } from './liveAccount'
import type { ParamDef, ParamValue } from './types'
import { registerSessionReset } from '@/app/lib/sessionReset'

/**
 * Per-strategy Config form state, held in memory for the page's lifetime so
 * switching strategies keeps each form's edits (lost on reload, by design:
 * spec 036 persists only the two Manual-trade rail keys).
 */
export interface ConfigFormState {
  /** Raw control values: a boolean for `bool`, otherwise the text as typed. */
  raw: Record<string, ParamValue>
  /** The CLI flag text of the last time every field was valid. */
  lastValidFlags: string
}

const forms = new Map<string, ConfigFormState>()

export function getConfigForm(slug: string): ConfigFormState | undefined {
  return forms.get(slug)
}

export function saveConfigForm(slug: string, form: ConfigFormState): void {
  forms.set(slug, form)
}

const accountForms = new Map<string, AccountForm>()

/** Spec 041: the Paper account block's values per strategy, in memory like the config form. */
export function getAccountForm(slug: string): AccountForm | undefined {
  return accountForms.get(slug)
}

export function saveAccountForm(slug: string, form: AccountForm): void {
  accountForms.set(slug, form)
}

const loadedNotes = new Map<string, string[]>()

/** Spec 041 P7: the names "Load params into config" dropped, handed to the Config tab once. */
export function setLoadedNote(slug: string, dropped: string[]): void {
  if (dropped.length === 0) loadedNotes.delete(slug)
  else loadedNotes.set(slug, dropped)
}

/** Read once, then removed. */
export function takeLoadedNote(slug: string): string[] | undefined {
  const dropped = loadedNotes.get(slug)
  loadedNotes.delete(slug)
  return dropped
}

export function resetConfigForms(): void {
  forms.clear()
  accountForms.clear()
  loadedNotes.clear()
}

/** Raw control values at the strategy.toml defaults. */
export function defaultRaw(defs: ParamDef[]): Record<string, ParamValue> {
  const raw: Record<string, ParamValue> = {}
  for (const def of defs) {
    if (def.kind === 'bool') raw[def.name] = def.default === true
    else raw[def.name] = def.default === null ? '' : String(def.default)
  }
  return raw
}

/** The printable flag text for a set of raw values, or `null` when any field is invalid. */
export function flagsFor(defs: ParamDef[], raw: Record<string, ParamValue>): string | null {
  const { values, errors } = coerceAll(defs, raw)
  if (Object.keys(errors).length > 0) return null
  return formatCliLine(buildCliFlags(values, defs))
}

export function initialConfigForm(defs: ParamDef[]): ConfigFormState {
  const raw = defaultRaw(defs)
  return { raw, lastValidFlags: flagsFor(defs, raw) ?? '' }
}

/** Whether a field's raw value differs from its default (numerics compare by number, so `5.0` equals `5`). */
export function isParamChanged(def: ParamDef, raw: ParamValue): boolean {
  if (def.kind === 'bool') return raw !== (def.default === true)
  if (def.default === null) return String(raw).trim() !== ''
  if (def.kind === 'int' || def.kind === 'float') {
    const text = String(raw).trim()
    const n = Number(text)
    return text === '' || !Number.isFinite(n) || n !== Number(def.default)
  }
  return String(raw) !== String(def.default)
}

/** Printable default for the CHANGED marker. */
export function defaultLabel(def: ParamDef): string {
  return def.default === null ? '(none)' : String(def.default)
}

registerSessionReset(resetConfigForms)
